import React, { createContext, useContext, useState, ReactNode } from "react";
import { isArgentinaDateValid } from "@/utils/dateUtils";
import { clearUserScopeFromStorage } from "@/utils/scopeStorage";
import { readCachedShipments, LEGACY_SHIPMENT_KEYS } from "@/utils/shipmentInfos";
import { OperationShift } from "@/utils/operationShifts";
import { ShipmentInfo, MyCoordinationSvcGroup, DriverProfile } from "@/types";

// Interface para el scope del usuario
interface UserScope {
  role: string;
  companies: {
    id: number;
    name: string;
  }[];
  operations: {
    id: number;
    name: string;
    companyId: number;
    shifts?: OperationShift[] | null;
  }[];
  svcs: {
    id: number;
    name: string;
    code: string;
    operationId: number;
    availabilityDays: number[];
    availabilityShifts?: string[];
  }[];
}

interface DriverContextType {
  // Scope del usuario
  userScope: UserScope | null;
  setUserScope: (scope: UserScope | null) => void;
  clearUserScope: (userId?: string | null) => void;

  // Perfil del chofer (nombre, etc. desde nuestra DB)
  driverProfile: DriverProfile | null;
  setDriverProfile: (profile: DriverProfile | null) => void;
  clearDriverProfile: () => void;

  // Datos de envíos
  todayShipments: ShipmentInfo[];
  setTodayShipments: (list: ShipmentInfo[]) => void;
  tomorrowShipments: ShipmentInfo[];
  setTomorrowShipments: (list: ShipmentInfo[]) => void;
  updateShipmentStatus: (id: string, status: string) => void;

  // Coordinaciones
  todayCoordinations: MyCoordinationSvcGroup[] | null;
  setTodayCoordinations: (groups: MyCoordinationSvcGroup[] | null) => void;
  clearTodayCoordinations: () => void;
  tomorrowCoordinations: MyCoordinationSvcGroup[] | null;
  setTomorrowCoordinations: (groups: MyCoordinationSvcGroup[] | null) => void;
  clearTomorrowCoordinations: () => void;

  // Datos de disponibilidad
  lastFetchDate: string | null;
  setLastFetchDate: (date: string) => void;
  clearLastFetchDate: () => void;
  isDataValid: () => boolean;
  refreshAvailability: () => Promise<void>;
  isAvailable: boolean | null;
  setIsAvailable: (available: boolean | null) => void;
  clearIsAvailable: () => void;

  // Estado de la API
  isApiClientReady: boolean;
  setApiClientReady: (ready: boolean) => void;
}

export const DriverContext = createContext<DriverContextType | undefined>(undefined);

export const useDriver = () => {
  const context = useContext(DriverContext);
  if (context === undefined) {
    throw new Error("useDriver must be used within a DriverProvider");
  }
  return context;
};

interface DriverProviderProps {
  children: ReactNode;
}

export const DriverProvider: React.FC<DriverProviderProps> = ({ children }) => {
  // Scope del usuario
  const [userScope, setUserScopeState] = useState<UserScope | null>(null);

  // Perfil del chofer
  const [driverProfile, setDriverProfileState] = useState<DriverProfile | null>(null);

  // Datos de envíos
  const [todayShipments, setTodayShipmentsState] = useState<ShipmentInfo[]>([]);
  const [tomorrowShipments, setTomorrowShipmentsState] = useState<ShipmentInfo[]>([]);
  const [todayCoordinations, setTodayCoordinationsState] = useState<
    MyCoordinationSvcGroup[] | null
  >(null);
  const [tomorrowCoordinations, setTomorrowCoordinationsState] = useState<
    MyCoordinationSvcGroup[] | null
  >(null);

  // Datos de disponibilidad
  const [lastFetchDate, setLastFetchDateState] = useState<string | null>(null);
  const [isAvailable, setIsAvailableState] = useState<boolean | null>(null);

  // Estado de la API
  const [isApiClientReady, setApiClientReadyState] = useState<boolean>(false);

  // Funciones para el scope del usuario (persistencia vía scopeStorage en ApiClientConfig)
  const setUserScope = (scope: UserScope | null) => {
    setUserScopeState(scope);
  };

  const clearUserScope = (userId?: string | null) => {
    setUserScopeState(null);
    if (userId != null) {
      clearUserScopeFromStorage(userId);
    }
  };

  // Funciones para el perfil del chofer
  const setDriverProfile = (profile: DriverProfile | null) => {
    setDriverProfileState(profile);
    if (profile) {
      localStorage.setItem("driverProfile", JSON.stringify(profile));
    } else {
      localStorage.removeItem("driverProfile");
    }
  };

  const clearDriverProfile = () => {
    setDriverProfileState(null);
    localStorage.removeItem("driverProfile");
  };

  // Funciones para envíos
  const persistShipments = (key: "todayShipments" | "tomorrowShipments", list: ShipmentInfo[]) => {
    if (list.length > 0) {
      localStorage.setItem(key, JSON.stringify(list));
    } else {
      localStorage.removeItem(key);
    }
  };

  const setTodayShipments = (list: ShipmentInfo[]) => {
    setTodayShipmentsState(list);
    persistShipments("todayShipments", list);
  };

  const setTomorrowShipments = (list: ShipmentInfo[]) => {
    setTomorrowShipmentsState(list);
    persistShipments("tomorrowShipments", list);
  };

  /** Actualiza el estado de UNA ruta (confirmar, cancelar, iniciar, finalizar). */
  const updateShipmentStatus = (id: string, status: string) => {
    const patch = (list: ShipmentInfo[]) =>
      list.map((s) => (s.id === id ? { ...s, status } : s));
    setTodayShipmentsState((prev) => {
      const next = patch(prev);
      persistShipments("todayShipments", next);
      return next;
    });
    setTomorrowShipmentsState((prev) => {
      const next = patch(prev);
      persistShipments("tomorrowShipments", next);
      return next;
    });
  };

  const setTodayCoordinations = (groups: MyCoordinationSvcGroup[] | null) => {
    setTodayCoordinationsState(groups);
    if (groups) {
      localStorage.setItem("todayCoordinations", JSON.stringify(groups));
    } else {
      localStorage.removeItem("todayCoordinations");
    }
  };

  const clearTodayCoordinations = () => {
    setTodayCoordinationsState(null);
    localStorage.removeItem("todayCoordinations");
  };

  const setTomorrowCoordinations = (groups: MyCoordinationSvcGroup[] | null) => {
    setTomorrowCoordinationsState(groups);
    if (groups) {
      localStorage.setItem("tomorrowCoordinations", JSON.stringify(groups));
    } else {
      localStorage.removeItem("tomorrowCoordinations");
    }
  };

  const clearTomorrowCoordinations = () => {
    setTomorrowCoordinationsState(null);
    localStorage.removeItem("tomorrowCoordinations");
  };

  // Funciones para disponibilidad
  const setLastFetchDate = (date: string) => {
    setLastFetchDateState(date);
    localStorage.setItem("lastFetchDate", date);
  };

  const clearLastFetchDate = () => {
    setLastFetchDateState(null);
    localStorage.removeItem("lastFetchDate");
  };

  const setIsAvailable = (available: boolean | null) => {
    setIsAvailableState(available);
    localStorage.setItem("isAvailable", String(available));
  };

  const clearIsAvailable = () => {
    setIsAvailableState(null);
    localStorage.removeItem("isAvailable");
  };

  const setApiClientReady = (ready: boolean) => {
    setApiClientReadyState(ready);
  };

  const refreshAvailability = async () => {
    // Simplemente limpiar el cache para forzar una nueva consulta
    clearLastFetchDate();
  };

  const isDataValid = () => {
    if (!lastFetchDate) return false;
    return isArgentinaDateValid(lastFetchDate);
  };

  // Cargar datos desde localStorage al inicializar (scope se carga en ApiClientConfig con cache/refetch)
  React.useEffect(() => {
    // Cargar datos de envíos
    // Claves de la época de "un viaje por día": se descartan, el fetch las reescribe.
    for (const key of LEGACY_SHIPMENT_KEYS) localStorage.removeItem(key);

    const cachedToday = readCachedShipments("todayShipments");
    if (cachedToday) setTodayShipmentsState(cachedToday);

    const cachedTomorrow = readCachedShipments("tomorrowShipments");
    if (cachedTomorrow) setTomorrowShipmentsState(cachedTomorrow);

    const savedLastFetchDateForCoord = localStorage.getItem("lastFetchDate");
    const coordCacheValid =
      savedLastFetchDateForCoord !== null &&
      isArgentinaDateValid(savedLastFetchDateForCoord);

    const savedTodayCoord = localStorage.getItem("todayCoordinations");
    if (savedTodayCoord && coordCacheValid) {
      try {
        setTodayCoordinationsState(JSON.parse(savedTodayCoord));
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error("Error parsing todayCoordinations:", error);
        }
        localStorage.removeItem("todayCoordinations");
      }
    } else if (savedTodayCoord && !coordCacheValid) {
      localStorage.removeItem("todayCoordinations");
    }

    const savedTomorrowCoord = localStorage.getItem("tomorrowCoordinations");
    if (savedTomorrowCoord && coordCacheValid) {
      try {
        setTomorrowCoordinationsState(JSON.parse(savedTomorrowCoord));
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error("Error parsing tomorrowCoordinations:", error);
        }
        localStorage.removeItem("tomorrowCoordinations");
      }
    } else if (savedTomorrowCoord && !coordCacheValid) {
      localStorage.removeItem("tomorrowCoordinations");
    }

    // Cargar datos de disponibilidad
    const savedLastFetchDate = localStorage.getItem("lastFetchDate");
    if (savedLastFetchDate) {
      setLastFetchDateState(savedLastFetchDate);
    }

    const savedIsAvailable = localStorage.getItem("isAvailable");
    if (savedIsAvailable) {
      setIsAvailableState(JSON.parse(savedIsAvailable));
    }

    const savedDriverProfile = localStorage.getItem("driverProfile");
    if (savedDriverProfile) {
      try {
        setDriverProfileState(JSON.parse(savedDriverProfile));
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error("Error parsing driverProfile from localStorage:", error);
        }
        localStorage.removeItem("driverProfile");
      }
    }
  }, []);

  return (
    <DriverContext.Provider
      value={{
        // Scope del usuario
        userScope,
        setUserScope,
        clearUserScope,

        // Perfil del chofer
        driverProfile,
        setDriverProfile,
        clearDriverProfile,

        // Datos de envíos
        todayShipments,
        setTodayShipments,
        tomorrowShipments,
        setTomorrowShipments,
        updateShipmentStatus,

        // Coordinaciones
        todayCoordinations,
        setTodayCoordinations,
        clearTodayCoordinations,
        tomorrowCoordinations,
        setTomorrowCoordinations,
        clearTomorrowCoordinations,

        // Datos de disponibilidad
        lastFetchDate,
        setLastFetchDate,
        clearLastFetchDate,
        isDataValid,
        isAvailable,
        setIsAvailable,
        clearIsAvailable,

        // Estado de la API
        isApiClientReady,
        setApiClientReady,
        refreshAvailability,
      }}
    >
      {children}
    </DriverContext.Provider>
  );
};
